import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store";
import { getProfile } from "../Redux/custmoer_auth/slice";
import { getCookie } from "../utility/getCookie";
import { Customer } from "../Redux/custmoer_auth/types";

// Mirrors the same guard used in the slice — avoids importing across boundary
const isRealToken = (t: string | null | undefined): t is string =>
  !!t && t !== "undefined" && t !== "null" && t.length > 20;

interface AuthContextType {
  user: Customer | null;
  isAuthenticated: boolean;
  profileLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, profileLoading } = useSelector(
    (state: RootState) => state.customerAuth,
  );
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = getCookie("access_token") || localStorage.getItem("access_token");

      if (!isRealToken(token)) {
        // Token is missing or corrupted — clear storage and proceed as guest
        if (token) {
          console.warn("[AuthContext] Found invalid token, clearing storage:", token);
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          document.cookie = "access_token=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/";
          document.cookie = "refresh_token=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/";
        }
        setIsChecking(false);
        return;
      }

      if (!user) {
        const result = await dispatch(getProfile());
        if (getProfile.rejected.match(result)) {
          const errMsg = String(result.payload || "").toLowerCase();
          const isAuthError =
            errMsg.includes("unauthorized") ||
            errMsg.includes("invalid") ||
            errMsg.includes("expired") ||
            errMsg.includes("jwt") ||
            errMsg.includes("not found");

          if (isAuthError) {
            console.error(
              "[AuthContext] getProfile failed with auth error — clearing invalid token:",
              result.payload,
            );
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            document.cookie =
              "access_token=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/";
            document.cookie =
              "refresh_token=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/";
          }
        }
      }
      setIsChecking(false);
    };
    checkAuth();
  }, [dispatch, user]);

  if (isChecking) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">
          <div className="spinner-border text-dark" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        profileLoading: isChecking || (!user && profileLoading),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
